import React, { useEffect, useRef, useState } from "react";

// Styles and Components
import { Button, Modal, Tooltip } from "@toddle-edu/ds-web";
import classes from "./AIModuleMarketingModal.scss";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  CalendarStarOutlined,
  ChevronLeftCircleOutlined,
  ChevronLeftOutlined,
  ChevronRightCircleOutlined,
  MagicWandThreeStarsOutlined,
} from "@toddle-edu/ds-icons";

// Redux and Hooks
import useI18n from "Hooks/UseI18n";
import { useDispatch } from "Hooks/useDispatch";
import {
  setAiModuleMarketingKey,
  sendChannelMessageForAIMarketing,
  trackAIMarketingModalOpenEvent,
} from "Platform/modules/PlatformModule";

// Utils
import _ from "lodash";
import {
  AI_MODULE_MARKETING_UI_CONFIGURATIONS,
  AI_MODULES_FOR_MARKETING,
  AI_MAKERTING_EVENTS,
} from "./AIModuleMarketingUtils";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import { connect } from "react-redux";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { compose, graphql } from "lib/apolloClient";
import { getDefaultAccountManagerDetailsQuery } from "Platform/modules/PlatformQuery";
import {
  getOrganizationCurriculumPrograms,
  getOrganizationId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import Loading from "UIComponents/Loading";
import moment from "moment";
import { getSettingValue } from "modules/PermissionModule";
import { updateSettings } from "modules/ConfigurationThunks";
import { Trans } from "react-i18next";

const styles = {
  mainModalHeaderStyles: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: "4px",
  },
  mainModalBodyStyles: {
    paddingLeft: 0,
    paddingRight: 0,
    overflowX: "hidden",
    paddingTop: "32px",
  },
  notifyAdminBodyStyles: { padding: "32px" },
  notifyAdminModalHeaderStyles: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: "8px",
  },
  hubspotModalHeaderStyles: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: "12px 28px",
    zIndex: 1,
    width: "100%",
  },
  hubspotModalBodyStyles: {
    padding: "0px",
    zIndex: 0,
  },
  headingTooltipStyles: { zIndex: 3005 },
};

const AIModuleMarketingModal = props => {
  // Get UI configurations for the module, like title, icon, slides, etc.
  const uiConfigurations =
    _.get(AI_MODULE_MARKETING_UI_CONFIGURATIONS, props.moduleKey) || {};

  const { t } = useI18n(["ai", "common", "a11y"]);
  const dispatch = useDispatch();
  const [activeSlide, setActiveSlide] = useState(0);
  const [showHubspotModal, setShowHubspotModal] = useState(false);
  const [showAdminNotifyModal, setShowAdminNotifyModal] = useState(false);
  const [showBookingDetailsModal, setShowBookingDetailsModal] = useState(false);
  const sliderRef = useRef(null);

  const onClose = () => {
    if (showAdminNotifyModal) setShowAdminNotifyModal(false);
    if (showHubspotModal) setShowHubspotModal(false);
    if (showBookingDetailsModal) setShowBookingDetailsModal(false);
    dispatch(setAiModuleMarketingKey(""));
    setTimeout(() => setActiveSlide(0), 100);
  };

  useEffect(() => {
    if (!_.isEmpty(props.moduleKey)) {
      dispatch(trackAIMarketingModalOpenEvent(props.moduleKey));
    }
  }, [props.moduleKey]);

  useEffect(() => {
    const onMessageReceived = event => {
      if (
        event.origin !== "https://meetings.hubspot.com" ||
        _.isEmpty(props.moduleKey)
      ) {
        return;
      }
      if (_.get(event, "data.meetingBookSucceeded", false)) {
        const meetingDetailsResponse = _.get(
          event,
          "data.meetingsPayload.bookingResponse",
          {}
        );
        const meetingData = {
          dateTime: moment(
            _.get(meetingDetailsResponse, "event.dateTime", ""),
            "x"
          ).format("lll"),
          duration: moment
            .duration(_.get(meetingDetailsResponse, "event.duration", ""))
            .minutes(),
          host: _.get(
            meetingDetailsResponse,
            "postResponse.organizer.name",
            props.programOwnerMeetingLink
          ),
        };

        dispatch(
          updateSettings({
            settings: [
              {
                name: props.bookedDateConfigKey,
                value: JSON.stringify({
                  dateTime: _.get(meetingDetailsResponse, "event.dateTime", ""),
                }),
              },
            ],
            showToastMsg: false,
          })
        );
        dispatch(
          sendChannelMessageForAIMarketing(
            AI_MAKERTING_EVENTS.MEETING_BOOKED,
            meetingData
          )
        );
      }
    };

    window.addEventListener("message", onMessageReceived);

    return () => {
      window.removeEventListener("message", onMessageReceived);
    };
  }, [props.moduleKey]);

  const onUpgradeClick = () => {
    const isAdmin = getIsAdminByRole();

    if (!isAdmin) {
      setShowAdminNotifyModal(true);
      dispatch(
        sendChannelMessageForAIMarketing(AI_MAKERTING_EVENTS.UPGRADE_NOW_CLICK)
      );
      return;
    }
    // For admin, open hubspot iframe if the meeting is not already booked
    if (!_.isNil(_.get(props.meetingBookedDate, "dateTime"))) {
      const isMeetingInFuture = moment(
        _.get(props.meetingBookedDate, "dateTime"),
        "x"
      ).isSameOrAfter(moment());
      if (isMeetingInFuture) {
        setShowBookingDetailsModal(true);
        return;
      } else {
        dispatch(
          updateSettings({
            settings: [
              {
                name: props.bookedDateConfigKey,
                value: JSON.stringify({}),
              },
            ],
            showToastMsg: false,
          })
        );
      }
    }

    setShowHubspotModal(true);
    dispatch(
      sendChannelMessageForAIMarketing(AI_MAKERTING_EVENTS.CHAT_WITH_US_CLICK)
    );
  };

  const getThumbStyles = () => {
    return {
      width: `${100 / _.size(uiConfigurations.slides)}%`,
      minHeight: "4px",
      color: "transparent",
      borderRadius: "2px",
      marginTop: "-1.5px",
      backgroundColor: `var(--border-bold-primary)`,
      transform: `translateX(${100 * activeSlide}%)`,
      transition: "transform 0.3s ease",
      cursor: "pointer",
    };
  };

  const onNextSlideClick = e => {
    e?.stopPropagation();
    if (activeSlide === _.size(uiConfigurations.slides) - 1) return;
    sliderRef.current.slickNext();
    sliderRef.current.slickPause();
  };

  const onPreviousSlideClick = e => {
    e?.stopPropagation();
    if (activeSlide === 0) return;
    sliderRef.current.slickPrev();
    sliderRef.current.slickPause();
  };

  const slideTo = index => {
    sliderRef.current.slickGoTo(index);
  };

  if (_.isEmpty(uiConfigurations)) {
    return null;
  }

  const onHubSpotbackButtonClick = () => {
    setShowHubspotModal(false);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    dotsClass: classes.sliderFooter,
    className: classes.slickSlider,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    appendDots: dots => (
      <div>
        <div className={classes.sliderNavButtonsContainer}>
          <ChevronLeftCircleOutlined
            iconWeight="normal"
            size="small"
            className={classes.sliderNavButton}
            disabled={activeSlide === 0}
            onClick={onPreviousSlideClick}
            onKeyDown={onPreviousSlideClick}
            tabIndex={0}
            data-test-id={"ai-marketing-modal-previous-slide-button"}
          />
          <ChevronRightCircleOutlined
            iconWeight="normal"
            size="small"
            className={classes.sliderNavButton}
            disabled={activeSlide === _.size(uiConfigurations.slides) - 1}
            onClick={onNextSlideClick}
            onKeyDown={onNextSlideClick}
            tabIndex={0}
            data-test-id={"ai-marketing-modal-next-slide-button"}
          />
        </div>
        <div className={classes.dots}>
          {/* --- Active thumb --- */}
          <div style={getThumbStyles(activeSlide)} />
          {/* Following buttons will act as an invisible dots, used to make the slider clickable/draggable to specific slide */}
          <div className={classes.invisibleDotsContainer}>
            {_.map(dots, (__, index) => (
              <button
                key={`dot-` + index}
                className={classes.invisibleDot}
                onClick={() => slideTo(index)}
                onMouseUp={() => slideTo(index)}
                data-test-id={`ai-marketing-modal-dot-${index}`}
              >
                {index} {/* For accessibility */}
              </button>
            ))}
          </div>
        </div>
      </div>
    ),
    beforeChange: (current, next) => {
      setActiveSlide(next);
    },
    centerMode: true,
    centerPadding: "36px",
  };

  return (
    <>
      {/* --- Main Slide show Modal --- */}
      <Modal
        width={"max(45vw, 740px)"}
        height={"max(90vh, 712px)"}
        isOpen={
          _.has(AI_MODULES_FOR_MARKETING, props.moduleKey) &&
          !_.isEmpty(uiConfigurations)
        }
        onClose={onClose}
        zIndex={3000}
        dsVersion={"2.0"}
        testId="ai-marketing-modal"
      >
        <Modal.Header
          isClosable
          style={styles.mainModalHeaderStyles}
          testId="ai-marketing-modal-header"
        />
        <Modal.Body style={styles.mainModalBodyStyles}>
          <div className={classes.container}>
            {/* --- Header --- */}
            <div className={classes.header}>
              <div className={classes.headerTitle}>
                <Tooltip
                  tooltip={t("ai:module_marketing_heading")}
                  placement="top"
                  overlayStyle={styles.headingTooltipStyles}
                  dsVersion="2.0"
                >
                  <p className={classes.heading}>
                    {t("ai:module_marketing_heading")}
                  </p>
                </Tooltip>
                <Tooltip
                  tooltip={t(_.get(uiConfigurations, "header.subTitle"))}
                  placement="top"
                  overlayStyle={styles.headingTooltipStyles}
                  dsVersion="2.0"
                >
                  <p className={classes.subHeading}>
                    {t(_.get(uiConfigurations, "header.subTitle"))}
                  </p>
                </Tooltip>
              </div>
              <Button
                dsVersion="2.0"
                variant="neutral"
                onClick={onUpgradeClick}
                size="large"
                tabIndex={0}
                testId={"ai-marketing-modal-upgrade-now"}
              >
                {getIsAdminByRole()
                  ? t("common:book_a_demo")
                  : t("common:upgrade_now")}
              </Button>
            </div>
            {/* --- Slides --- */}
            <div
              className={classes.slidesContainer}
              data-test-id={"ai-marketing-slides-container"}
            >
              <Slider {...sliderSettings} ref={sliderRef}>
                {_.map(uiConfigurations.slides, (slide, index) => (
                  <div key={`${uiConfigurations.key}-${index}`}>
                    <div
                      className={classes.slide}
                      style={{
                        backgroundColor: `var(${uiConfigurations.variant})`,
                      }}
                      data-test-id={`ai-marketing-slide-${index}`}
                      data-is-active-slide={activeSlide === index}
                    >
                      <div className={classes.slideImageContainer}>
                        <img
                          className={classes.slideImage}
                          src={slide.imageUrl}
                          alt={slide.title}
                          style={slide.imageStyles}
                        />
                      </div>
                      <p className="text-label-1 mt-2 mb-4 ml-3 mr-3 text-center">
                        {t(slide.title)}
                      </p>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {/* --- "Notify to Admin" Modal --- */}
      <Modal
        dsVersion={"2.0"}
        hasOverlay={true}
        height={200}
        width={400}
        isClosable={true}
        onClose={onClose}
        isOpen={showAdminNotifyModal}
        zIndex={3001}
        testId="ai-marketing-modal-notify-admin"
      >
        <Modal.Header isClosable style={styles.notifyAdminModalHeaderStyles} />
        <Modal.Body style={styles.notifyAdminBodyStyles}>
          <div className={classes.notifyAdminHeader}>
            <div className={classes.magicWandContainer}>
              <MagicWandThreeStarsOutlined variant="link" size="x-small" />
            </div>
            <h5>{t("common:admin_needs_to_upgrade")}</h5>
          </div>
          <div>
            <p
              className="text-body m-0 mt-6"
              style={styles.secondaryTextStyles}
            >
              {t("common:admin_upgrade_body_message")}
            </p>
          </div>
        </Modal.Body>
      </Modal>
      {/* --- Hubspot iframe Modal (conditionally rendered so that iframe gets reset) --- */}
      {showHubspotModal && (
        <Modal
          isOpen={showHubspotModal}
          onClose={onClose}
          zIndex={3002}
          isClosable={true}
          width={"max(45vw, 740px)"}
          height={"max(90vh, 712px)"}
          hasOverlay={false}
        >
          <Modal.Header isClosable style={styles.hubspotModalHeaderStyles}>
            <Button
              icon={<ChevronLeftOutlined />}
              type="plain"
              dsVersion="2.0"
              onClick={onHubSpotbackButtonClick}
            >
              {t("common:back")}
            </Button>
          </Modal.Header>
          <Modal.Body style={styles.hubspotModalBodyStyles}>
            {props.meetingUrlLoading ? (
              <Loading />
            ) : (
              <iframe
                src={props.meetingUrl}
                width="100%"
                height="100%"
                style={{
                  backgroundColor: "#f5f8fa",
                  padding: "16px 16px 0 16px",
                }}
                title={t("a11y:schedule_ai_module_demo_call")}
              />
            )}
          </Modal.Body>
        </Modal>
      )}
      {/* --- Booking Details Modal --- */}
      <Modal
        dsVersion={"2.0"}
        hasOverlay={true}
        height={200}
        width={400}
        isClosable={true}
        onClose={onClose}
        isOpen={showBookingDetailsModal}
        zIndex={3001}
        testId="ai-marketing-modal-meeting-booked"
      >
        <Modal.Header isClosable style={styles.notifyAdminModalHeaderStyles} />
        <Modal.Body style={styles.notifyAdminBodyStyles}>
          <div className={classes.notifyAdminHeader}>
            <div className={classes.magicWandContainer}>
              <CalendarStarOutlined variant="link" size="x-small" />
            </div>
            <h5>{t("ai:demo_call_scheduled_heading")}</h5>
          </div>
          <div>
            <p
              className="text-body m-0 mt-6"
              style={styles.secondaryTextStyles}
            >
              <Trans i18nKey={"ai:demo_call_scheduled_subtitle"}>
                <b>
                  {{
                    label: moment(
                      _.get(props, "meetingBookedDate.dateTime"),
                      "x"
                    ).format("lll"),
                  }}
                </b>
              </Trans>
            </p>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

const mapStateToProps = state => {
  const aiModuleMarketingKey = _.get(state, "platform.aiModuleMarketingKey");
  const userInfo = getUserInfo();

  const bookedDateConfigKey =
    aiModuleMarketingKey === AI_MODULES_FOR_MARKETING.AI_TUTOR
      ? "AITutorMarketingDemoBookedDate"
      : "AIMarketingDemoBookedDate";
  const meetingBookedDate = getSettingValue({
    name: bookedDateConfigKey,
    userId: userInfo?.id,
    readFromUserAndOrgLevel: true,
  });

  let curriculumProgramId = getSelectedCurriculumId(state);

  if (_.isNil(curriculumProgramId) || _.isEmpty(curriculumProgramId)) {
    // As a fallback, get the first curriculum program id
    curriculumProgramId = _.get(
      _.first(getOrganizationCurriculumPrograms(state)),
      "id",
      ""
    );
  }

  return {
    orgId: getOrganizationId(state),
    currentCurriculumId: curriculumProgramId,
    moduleKey: aiModuleMarketingKey,
    meetingUrlQueryParams: `firstName=${userInfo.first_name}&lastName=${userInfo.last_name}&email=${userInfo.email}&inbound_school_name=${userInfo.org_name}`,
    meetingBookedDate,
    bookedDateConfigKey,
  };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getDefaultAccountManagerDetailsQuery, {
    name: "getDefaultAccountManagerDetails",
    options: () => ({
      fetchPolicy: "cache-and-network",
      variables: {
        portalType: "PLANNER",
      },
    }),
    props: ({ getDefaultAccountManagerDetails, ownProps }) => {
      const accountManagerDetails = _.get(
        getDefaultAccountManagerDetails,
        "globalConstants.defaultAccountMangerDetails",
        {}
      );
      const programOwnerLink = _.get(accountManagerDetails, "meetingUrl", "");
      return {
        programOwnerLink,
        meetingUrl: `${programOwnerLink}?${ownProps.meetingUrlQueryParams}`,
        meetingUrlLoading: _.includes(
          [1, 2],
          _.get(getDefaultAccountManagerDetails, "networkStatus")
        ),
      };
    },
  })
)(AIModuleMarketingModal);
